// src/components/Slots.js
import React, { useState, useEffect } from "react";
import { standardUserApi } from "../services/standardUserApi";
import mixpanel from "mixpanel-browser";
import HelpFeedback from "./HelpFeedback";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import GatheringsList from "./GatheringsList";
import ThemesDropdown from "./ThemesDropdown";
import useNoIndex from "../hooks/useNoIndex";
import { trackPageView, trackSlotEvent, setUserIdProperty, getUserId } from "../utils/tracking";
import LoadingState from "./LoadingState";

// NEW imports to mirror FoundGathering.js subscription logic
import memberstackDOM from "@memberstack/dom";
import { getMemberstackKey } from "../utils";

const currentKey = getMemberstackKey();
const memberstack = memberstackDOM.init({
  publicKey: currentKey,
});

const Slots = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Prevent search engines from indexing this member-only page
  useNoIndex();

  // Core booking state
  const [selectedSlotIds, setSelectedSlotIds] = useState([]);
  const [selectedSlots, setSelectedSlots] = useState([]); // Store full gathering data
  const [hasActivePaidSubscription, setHasActivePaidSubscription] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isVerifyingAuth, setIsVerifyingAuth] = useState(true); // Add auth verification state
  const [themes, setThemes] = useState([]);

  // Read selected theme from sessionStorage (set by ThemesDropdown or ThemeLandingPage)
  const [initialThemeId, setInitialThemeId] = useState(null);
  const [initialThemeName, setInitialThemeName] = useState('');
  const [isWeeklyTheme, setIsWeeklyTheme] = useState(false);

  // Check URL parameter first, update sessionStorage, and load theme
  useEffect(() => {
    // Check if theme is passed via URL parameter
    const themeParam = searchParams.get('theme');
    if (themeParam) {
      // If theme is in URL, update sessionStorage to override any previously stored theme
      sessionStorage.setItem('selectedThemeId', themeParam);
      // Clear the theme name so it will be fetched fresh
      sessionStorage.removeItem('selectedThemeName');
      sessionStorage.removeItem('selectedThemeWeekly');
    }

    // Now load from sessionStorage (which may have just been updated)
    const storedThemeId = sessionStorage.getItem('selectedThemeId');
    const storedThemeName = sessionStorage.getItem('selectedThemeName');
    const storedWeekly = sessionStorage.getItem('selectedThemeWeekly');

    if (storedThemeId) {
      setInitialThemeId(parseInt(storedThemeId));
      setInitialThemeName(storedThemeName || '');
      setIsWeeklyTheme(storedWeekly === 'true');
    }
  }, [searchParams]);

  // Load themes on mount
  useEffect(() => {
    const loadThemes = async () => {
      try {
        const lookupData = await standardUserApi.getLookupData();
        setThemes(lookupData.themes || []);

        // If we have an initial theme ID but don't know if it's weekly, check from themes
        const storedThemeId = sessionStorage.getItem('selectedThemeId');
        if (storedThemeId) {
          const theme = (lookupData.themes || []).find(t => t.id === parseInt(storedThemeId));
          if (theme) {
            setIsWeeklyTheme(theme.weekly || false);
          }
        }
      } catch (error) {
        console.error("Failed to load themes:", error);
      }
    };
    loadThemes();
  }, []);

  // Validate auth and check subscription on mount
  useEffect(() => {
    async function checkSubscription() {
      const token = localStorage.getItem("memberstackToken");

      // First, validate session
      if (!token) {
        setShowLoginModal(true);
        setIsVerifyingAuth(false);
        return;
      }

      try {
        const currentMember = await memberstack.getCurrentMember();

        if (!currentMember || !currentMember.data) {
          setShowLoginModal(true);
          setIsVerifyingAuth(false);
          return;
        }

        // Logged-in users are redirected to their events page
        navigate("/my-events");
        return;
      } catch (error) {
        setShowLoginModal(true);
        setIsVerifyingAuth(false);
      }
    }
    checkSubscription();
  }, []);

  // Track form view event on component mount
  useEffect(() => {
    const trackFormView = async () => {
      setUserIdProperty();

      const hasReferralCode = localStorage.getItem('referralCode');

      // Track with theme context
      trackPageView('Slots Page', {
        theme_id: initialThemeId,
        theme_name: initialThemeName,
        is_weekly_theme: isWeeklyTheme,
        source: searchParams.get('source') || 'direct',
        has_referral_code: !!hasReferralCode,
        referral_code: hasReferralCode || null
      });

      // Track referral funnel step if user came from referral
      if (hasReferralCode && mixpanel && mixpanel.__loaded) {
        mixpanel.track('Referral Funnel: Slots Viewed', {
          funnel_step: 'slots',
          funnel_step_number: 4,
          referral_code: hasReferralCode,
          theme_id: initialThemeId,
          theme_name: initialThemeName
        });
      }
    };
    trackFormView();
  }, [initialThemeId, initialThemeName, isWeeklyTheme, searchParams]);

  // Session validation is now handled in the checkSubscription useEffect above

  const handleCheckboxChange = (slotData) => {
    // Handle both old format (just slotId) and new format (slotData object)
    const slotId = typeof slotData === 'object' ? slotData.slotId : slotData;

    if (typeof slotData === 'object') {
      // New format with gathering details - single selection mode
      setSelectedSlots((prev) => {
        const exists = prev.find(s => s.slotId === slotId);
        if (exists) {
          // Deselect - remove this slot
          return prev.filter(s => s.slotId !== slotId);
        } else {
          // Single-selection mode
          return [slotData];
        }
      });
    }

    setSelectedSlotIds((prev) => {
      const isSelected = prev.includes(slotId);
      let next;

      // Single-selection mode
      next = isSelected
        ? []  // Deselect - clear all
        : [slotId];  // Select - only this slot

      // Track selection/deselection with theme data
      const action = isSelected ? "Deselected" : "Selected";
      trackSlotEvent(action, {
        slotId,
        themeId: typeof slotData === 'object' ? slotData.themeId : null,
        themeName: typeof slotData === 'object' ? slotData.themeName : null,
        datetime: typeof slotData === 'object' ? slotData.datetime : null
      }, {
        selection_count: next.length,
        is_weekly_theme: isWeeklyTheme
      });

      return next;
    });
  };

  const handleConfirm = async () => {
    if (selectedSlotIds.length === 0) {
      alert("Please select a slot before confirming.");
      trackSlotEvent("Confirm Failed", {}, {
        reason: "no_slot_selected"
      });
      return;
    }

    try {
      const token = localStorage.getItem("memberstackToken");
      if (!token) {
        alert("No token found. Please log in.");
        trackSlotEvent("Confirm Failed", {}, {
          reason: "no_token"
        });
        return;
      }

      // Build payload from selectedSlots array
      const payload = {
        slots: selectedSlots.map(s => ({
          datetime: s.datetime,
          theme_id: s.themeId,
          location_id: s.locationId || null,
        }))
      };

      console.log("Booking payload:", payload);

      await standardUserApi.updateUserSlots(token, payload);

      sessionStorage.setItem('justBooked', 'true');
      // Store the selected slots' theme_ids for subscription check on find-gathering page
      sessionStorage.setItem('selectedThemeIds', JSON.stringify(
        selectedSlots.map(s => s.themeId)
      ));
      // Also store first theme ID for backward compatibility
      sessionStorage.setItem('selectedThemeId', selectedSlots[0].themeId);

      // Track successful confirmation with full context
      trackSlotEvent("Booking Confirmed", {
        slotId: selectedSlotIds[0],
        themeId: selectedSlots[0].themeId,
        themeName: selectedSlots[0].themeName || initialThemeName,
        datetime: selectedSlots[0].datetime
      }, {
        slot_count: selectedSlotIds.length,
        all_slot_ids: selectedSlotIds,
        all_theme_ids: selectedSlots.map(s => s.themeId),
        is_weekly_theme: isWeeklyTheme
      });

      // Check subscription status and route directly — skip the find-gathering page
      let hasSubscription = false;
      try {
        const member = await memberstack.getCurrentMember();
        (member.data?.planConnections || []).forEach(plan => {
          const st = plan.status?.toLowerCase();
          const tp = plan.type?.toLowerCase();
          if (tp === "subscription" && (st === "active" || st === "trialing")) {
            hasSubscription = true;
          }
        });
      } catch {
        // Fall back to the subscription state loaded on mount
        hasSubscription = hasActivePaidSubscription;
      }

      if (hasSubscription) {
        navigate("/my-events");
      } else {
        // Persist slot context for the checkout page
        localStorage.setItem("selectedSlotData", JSON.stringify({
          datetime: selectedSlots[0].datetime,
          themeId: selectedSlots[0].themeId,
          locationId: selectedSlots[0].locationId || null,
          locationCity: selectedSlots[0].locationCity || null,
          locationArea: selectedSlots[0].locationArea || null,
        }));
        navigate("/checkout");
      }

    } catch (error) {
      trackSlotEvent("Booking Error", {}, {
        error_message: error.message,
        slot_count: selectedSlotIds.length
      });
      console.error("Booking error:", error);
      alert("Failed to save slot. Please try again.");
    }
  };

  // Show loading state while verifying authentication
  if (isVerifyingAuth) {
    return <LoadingState />;
  }

  return (
    <div className="page-wrapper" style={{ position: "relative" }}>
      {/* Login Modal */}
      {showLoginModal && (
        <div
          style={{
            position: "fixed",
            top: 0, left: 0,
            width: "100vw", height: "100vh",
            backgroundColor: "rgba(0,0,0,0.6)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 9999
          }}
        >
          <div
            style={{
              background: "#fff",
              padding: "32px",
              borderRadius: "12px",
              textAlign: "center",
              maxWidth: "400px",
              width: "90%",
              boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
              margin: "20px"
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: "16px", color: "#333" }}>
              Please Log In
            </h3>
            <p style={{ marginBottom: "24px", color: "#666", lineHeight: "1.5" }}>
              Please login to book your events so you can meet new people.
            </p>
            <button
              className="button-2 is-black w-button"
              onClick={() => navigate("/login")}
              style={{ minWidth: "120px" }}
            >
              Continue
            </button>
          </div>
        </div>
      )}

      <main className="main-wrapper">
        <section className="section_hero">
          <div className="padding-global padding-section-large container-large">
            <div className="hero_card is-vertical" style={{ position: "relative" }}>
              <HelpFeedback />

              {(hasActivePaidSubscription || selectedSlotIds.length > 0) && (
                <Link
                  to="/edit-profile"
                  style={{
                    position: "absolute",
                    top: "10px",
                    left: "10px",
                    textDecoration: "none",
                    zIndex: 1100,
                  }}
                >
                  <button className="button-2 is-black w-button">
                    My Profile
                  </button>
                </Link>
              )}

              {!initialThemeId ? (
                // No theme selected - show message and theme selector
                <>
                  <h4 style={{ textAlign: "center", marginBottom: "16px", color: "#8B5FBF" }}>
                    Select a Theme to View Available Slots
                  </h4>
                  <p
                    className="homepage_hero_paragraph text-align-center"
                    style={{ marginBottom: "30px", maxWidth: "600px", margin: "0 auto 30px" }}
                  >
                    We couldn't find a selected theme. Please choose a gathering category below to view available time slots.
                  </p>
                  <ThemesDropdown />
                </>
              ) : (
                // Theme selected - show slot booking interface
                <>
                  <h4 style={{ textAlign: "center", marginBottom: "10px" }}>
                    When are you available to meet new people?
                  </h4>

                  <p
                    className="homepage_hero_paragraph text-align-center"
                    style={{ marginBottom: "10px" }}
                  >
                    You're nearly there! Remember...
                  </p>

                  <ul
                    className="homepage_hero_list"
                    style={{
                      listStyle: "none",
                      padding: 0,
                      margin: "0 auto 20px",
                      maxWidth: "700px",
                      textAlign: "center",
                    }}
                  >
                    <li style={{ fontWeight: "normal", marginBottom: "8px" }}>
                      You'll be joining a group of people for drinks 🪄
                    </li>
                    <li style={{ fontWeight: "normal", marginBottom: "8px" }}>
                      📍 We book the table for drinks at a great venue 🥂
                    </li>
                    <li style={{ fontWeight: "normal", marginBottom: "8px" }}>
                      🎉 All you have to do is show up and have a great time! 🥳
                    </li>
                  </ul>

                  {/* GatheringsList Component - handles all the complex slot logic */}
                  <GatheringsList
                    mode="booking"
                    maxItems={isWeeklyTheme ? 2 : 1} // 2 slots for weekly themes, 1 for monthly
                    selectedSlotIds={selectedSlotIds}
                    onSlotSelection={handleCheckboxChange}
                    initialThemeId={initialThemeId}
                    initialThemeName={initialThemeName}
                  />
                </>
              )}
            </div>
          </div>
        </section>
      </main>

      {/* Floating Confirm Button - only show when gatherings are selected */}
      {selectedSlotIds.length > 0 && (
        <div style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: "#fff",
          borderTop: "3px solid #8B5FBF",
          padding: "20px",
          boxShadow: "0 -4px 15px rgba(139, 95, 191, 0.15)",
          zIndex: 1000,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "12px"
        }}>
          <button
            className="button-2 is-black w-button"
            onClick={handleConfirm}
            style={{
              fontSize: "1.1rem",
              padding: "12px 24px",
              minWidth: "200px"
            }}
          >
            Continue
          </button>
        </div>
      )}
    </div>
  );
};

export default Slots;
